/**
 * GP-01 pile view: one TileView per board piece (tiles and crates), drawn by layer.
 * Covered pieces are 40% darker; wrapped tiles hide their item until revealed.
 * Dispensers (rules v2): a MachineView per machine, and a TileView for its top item only.
 * LockKey (rules v2): a padlock on each locked tile while the locks are shut, and a key badge
 * on every piece of the key item (read from core state after every change, so it follows a shuffle).
 * CollectGoal (rules v2): a golden piece wears a gold frame and a star corner (the marker belongs
 * to the piece, so a shuffle moves items under it and the frame stays); in the bag only the star.
 * Trays (rules v2): a TrayView per tray, drawn at its cell with its item three times; its pieces
 * have no TileView (they never fly to the bag), and a tap anywhere on it takes its first piece.
 * Clearer items: every item is set through `looks` (looks.ts), so a lookalike clutter item is
 * drawn with its substitute's art while the setting is on; `getData('item')` and the hooks that
 * report items keep the engine's item indices.
 * The view mirrors core state; it never decides legality.
 */
import * as Phaser from 'phaser';
import { Kind, PIECE_BOARD, isRevealed, type CompiledLevel, type Round } from '@mp/core';
import { app } from '../game/context.js';
import { piecePos, type PlayLayout } from '../game/layout.js';
import { deco, dur } from '../fx/motion.js';
import { boardPieces, machineOf, machineState } from './dispensers.js';
import { isGoldenPiece } from './goal.js';
import { GOLD_FRAME, GOLD_STAR, bakeGoldArt } from './goldFrame.js';
import { GlueRibbon } from './glue.js';
import { hasLocks, isKeyPiece, padlockTiles } from './keys.js';
import { NO_LOOKS, drawnArt, type ItemLooks } from './looks.js';
import { KEY_BADGE, TilePadlock, bakeLockArt } from './lock.js';
import { MachineView } from './machine.js';
import { gluePairs, ribbonState } from './pairs.js';
import { TrayView } from './tray.js';
import { trayOf, trayState } from './trays.js';

const COVERED_TINT = 0x999999; // 60% brightness = 40% darker (GP-01.1)

function lerpTint(k: number): number {
  const v = Math.round(0x99 + (0xff - 0x99) * k);
  return (v << 16) | (v << 8) | v;
}

export class TileView extends Phaser.GameObjects.Container {
  base: Phaser.GameObjects.Image;
  item: Phaser.GameObjects.Image | null = null;
  wrap: Phaser.GameObjects.Image | null = null;
  ice: Phaser.GameObjects.Image | null = null;
  /** lockKey: the padlock on a locked tile (while the locks are shut) */
  padlock: TilePadlock | null = null;
  /** lockKey: the key badge on a piece of the key item */
  keyBadge: Phaser.GameObjects.Image | null = null;
  /** collectGoal: the gold frame of a golden piece on the board */
  goldFrame: Phaser.GameObjects.Image | null = null;
  /** collectGoal: the star on a golden piece's bottom-left corner (kept in the bag) */
  goldStar: Phaser.GameObjects.Image | null = null;
  covered = false;
  private shade = 1;
  readonly size: number;

  constructor(
    scene: Phaser.Scene,
    readonly piece: number,
    readonly isCrate: boolean,
    size: number,
  ) {
    super(scene, 0, 0);
    this.size = size;
    const art = app().art;
    if (isCrate) {
      const f = art.get('prp_mech_crate_3');
      this.base = scene.add.image(0, 0, f.texture, f.frame);
      this.base.setDisplaySize(size * 0.98, size * 0.98);
    } else {
      const f = art.get('ui_play_tile');
      this.base = scene.add.image(0, 0, f.texture, f.frame);
      const k = (size * 0.96) / this.base.width;
      this.base.setScale(k);
      this.base.setOrigin(0.5, 96 / 204);
    }
    this.add(this.base);
    scene.add.existing(this);
  }

  /** Draw the art of `itemId`: the item's own, or its substitute's under Clearer items (the callers ask `looks.of`). */
  setItem(itemId: string): void {
    const f = app().art.item(itemId);
    if (!this.item) {
      this.item = this.scene.add.image(0, -this.size * 0.02, f.texture, f.frame);
      this.addAt(this.item, 1);
    } else this.item.setTexture(f.texture, f.frame);
    const s = (this.size * 0.76) / Math.max(this.item.width, this.item.height);
    this.item.setScale(s);
    this.applyShade();
  }

  /** The asset the item image shows (looks.ts drawnArt), null before an item is set (test hooks). */
  drawnArt(): { id: string; real: boolean } | null {
    return this.item ? drawnArt(this.item) : null;
  }

  setCrateHp(hp: number): void {
    if (!this.isCrate || hp <= 0) return;
    const f = app().art.get(`prp_mech_crate_${Math.min(3, hp)}`);
    this.base.setTexture(f.texture, f.frame);
    this.base.setDisplaySize(this.size * 0.98, this.size * 0.98);
  }

  setWrapped(show: boolean, animate: boolean): void {
    if (show && !this.wrap) {
      const f = app().art.get('prp_mech_wrap');
      this.wrap = this.scene.add.image(0, -this.size * 0.02, f.texture, f.frame).setDisplaySize(this.size * 0.9, this.size * 0.9);
      this.add(this.wrap);
      this.item?.setVisible(false);
      this.applyShade();
    } else if (!show && this.wrap) {
      const w = this.wrap;
      this.wrap = null;
      this.item?.setVisible(true);
      const d = animate ? dur(220) : 0;
      if (!d) {
        w.destroy();
        return;
      }
      this.item?.setScale(0.01, this.item.scaleY);
      const target = this.item ? (this.size * 0.76) / Math.max(this.item.width, this.item.height) : 1;
      this.scene.tweens.add({ targets: w, scaleX: 0, alpha: 0.4, duration: d / 2, onComplete: () => w.destroy() });
      if (this.item) this.scene.tweens.add({ targets: this.item, scaleX: target, delay: d / 2, duration: d / 2, ease: 'Back.easeOut' });
    }
  }

  setIce(n: number, animate: boolean): void {
    if (n > 0) {
      const f = app().art.get(`prp_mech_ice_${Math.min(2, n)}`);
      if (!this.ice) {
        this.ice = this.scene.add.image(0, -this.size * 0.02, f.texture, f.frame);
        this.add(this.ice);
      } else this.ice.setTexture(f.texture, f.frame);
      this.ice.setDisplaySize(this.size * 0.96, this.size * 0.96);
    } else if (this.ice) {
      const ice = this.ice;
      this.ice = null;
      if (!animate || !dur(1)) ice.destroy();
      else this.scene.tweens.add({ targets: ice, alpha: 0, scale: ice.scale * 1.3, duration: dur(200), onComplete: () => ice.destroy() });
    }
  }

  setCovered(covered: boolean, animate: boolean): void {
    if (covered === this.covered) return;
    this.covered = covered;
    const to = covered ? 0 : 1;
    const d = animate ? dur(180) : 0;
    if (!d) {
      this.shade = to;
      this.applyShade();
      return;
    }
    this.scene.tweens.addCounter({
      from: this.shade,
      to,
      duration: d,
      onUpdate: (tw) => {
        this.shade = tw.getValue() ?? to;
        this.applyShade();
      },
    });
  }

  /** lockKey: put the padlock on (removed only by `openPadlock`: an undo never re-locks). */
  setPadlock(): void {
    if (this.padlock) return;
    this.padlock = new TilePadlock(this.scene, this.size);
    this.add(this.padlock);
    this.applyShade();
  }

  /** lockKey: the key arrived; the padlock springs open and flies off. */
  openPadlock(onPop: ((x: number, y: number) => void) | null): Promise<void> {
    const p = this.padlock;
    this.padlock = null;
    return p ? p.open(onPop) : Promise.resolve();
  }

  /** lockKey: the key badge (top-left corner) on or off; it pops in and fades out. */
  setKey(on: boolean, animate: boolean): void {
    if (on === !!this.keyBadge) return;
    const d = animate ? dur(180) : 0;
    if (on) {
      bakeLockArt(this.scene);
      const k = this.scene.add.image(-this.size * 0.3, -this.size * 0.3, KEY_BADGE);
      const s = (this.size * 0.36) / k.width;
      k.setScale(d ? s * 0.2 : s);
      this.add(k);
      this.keyBadge = k;
      if (d) this.scene.tweens.add({ targets: k, scale: s, duration: d, ease: 'Back.easeOut' });
      this.applyShade();
      return;
    }
    const k = this.keyBadge!;
    this.keyBadge = null;
    if (!d) return void k.destroy();
    this.scene.tweens.add({ targets: k, scale: k.scale * 1.4, alpha: 0, duration: d, ease: 'Quad.easeOut', onComplete: () => k.destroy() });
  }

  /**
   * collectGoal: a golden piece on the board wears the gold frame (over the tile, under the item)
   * and the star on its bottom-left corner (top-left is lockKey's key badge, top-right the
   * colour-blind pattern mark of the item art); in the bag only the star stays, as a small gold
   * corner. Null takes both off.
   */
  setGolden(mode: 'board' | 'bag' | null): void {
    const frame = mode === 'board';
    if (frame === !!this.goldFrame && !!mode === !!this.goldStar) return;
    // (an intro glint may still be playing on them)
    if (!frame && this.goldFrame) {
      this.scene.tweens.killTweensOf(this.goldFrame);
      this.goldFrame.destroy();
      this.goldFrame = null;
    }
    if (!mode) {
      if (this.goldStar) this.scene.tweens.killTweensOf(this.goldStar);
      this.goldStar?.destroy();
      this.goldStar = null;
      return;
    }
    bakeGoldArt(this.scene);
    if (frame && !this.goldFrame) {
      const f = this.scene.add.image(0, 0, GOLD_FRAME);
      f.setScale((this.size * 0.96) / f.width);
      this.addAt(f, this.item ? this.getIndex(this.item) : 1);
      this.goldFrame = f;
    }
    if (!this.goldStar) {
      const s = this.scene.add.image(-this.size * 0.31, this.size * 0.29, GOLD_STAR);
      s.setScale((this.size * 0.38) / s.width);
      this.add(s);
      this.goldStar = s;
    }
    this.applyShade();
  }

  /** collectGoal: the frame and star glint once (mechanic intro): decorative only. */
  shineGolden(delay: number): void {
    const d = deco(260);
    const f = this.goldFrame;
    const s = this.goldStar;
    if (!d || !f || !s) return;
    const fs = f.scale;
    const ss = s.scale;
    // a five-point star turned by 72° looks the same: it spins once and settles where it was
    this.scene.tweens.add({ targets: f, scale: fs * 1.08, duration: d / 2, delay, yoyo: true, ease: 'Sine.easeOut', onComplete: () => f.setScale(fs) });
    this.scene.tweens.add({ targets: s, scale: ss * 1.35, duration: d / 2, delay, yoyo: true, ease: 'Quad.easeOut', onComplete: () => s.setScale(ss) });
    this.scene.tweens.add({ targets: s, angle: 72, duration: d, delay, ease: 'Cubic.easeOut', onComplete: () => s.setAngle(0) });
  }

  /** Dispenser window: the item alone behind the glass, no tile under it (it becomes a tile again as it leaves). */
  setBare(bare: boolean): void {
    this.base.setVisible(!bare);
  }

  /** Lit state for flights and the bag (always full brightness). */
  lightUp(): void {
    this.covered = false;
    this.shade = 1;
    this.applyShade();
  }

  private applyShade(): void {
    const tint = this.shade >= 1 ? 0xffffff : this.shade <= 0 ? COVERED_TINT : lerpTint(this.shade);
    this.base.setTint(tint);
    this.item?.setTint(tint);
    this.wrap?.setTint(tint);
    this.keyBadge?.setTint(tint);
    this.goldFrame?.setTint(tint);
    this.goldStar?.setTint(tint);
    if (this.padlock && !this.padlock.opening) for (const p of this.padlock.parts) p.setTint(tint);
  }

  /** lockKey: a tap on a padlocked tile — the tile wobbles and its padlock rattles. */
  lockNope(): void {
    this.nope();
    this.padlock?.rattle();
  }

  /** Soft "nope" wobble for covered taps (GP-01.1). */
  nope(): void {
    const d = dur(260);
    if (!d) return;
    this.scene.tweens.killTweensOf(this);
    this.setAngle(0);
    this.scene.tweens.add({
      targets: this,
      angle: { from: -7, to: 7 },
      duration: d / 4,
      yoyo: true,
      repeat: 1,
      ease: 'Sine.easeInOut',
      onComplete: () => this.setAngle(0),
    });
  }
}

export class BoardView {
  readonly views: (TileView | null)[];
  private tray: Phaser.GameObjects.Graphics;
  /** glue ribbons by the pair's lower tile (rules v2) */
  private readonly ribbons = new Map<number, GlueRibbon>();
  private readonly pairs: [number, number][];
  /** a ribbon snapped (its pair left the board): the scene adds the pop */
  onRibbonSnap: ((x: number, y: number) => void) | null = null;
  /** dispenser machines by index (rules v2) */
  readonly machines: MachineView[] = [];
  /** a machine ran empty and folds away: the scene adds the puff */
  onMachineGone: ((x: number, y: number) => void) | null = null;
  /** trays by index (rules v2); null once popped (a rebuild draws only the trays still on the board) */
  readonly trays: (TrayView | null)[] = [];
  /** a popped tray folds away: the scene adds the puff */
  onTrayGone: ((x: number, y: number) => void) | null = null;
  /** tray pops played on this board (test hooks) */
  trayPops = 0;
  /** tray pops still playing (`idle`) */
  private readonly trayJobs = new Set<Promise<void>>();
  /**
   * lockKey: the locks opened in core but the key has not popped on screen yet; the key badges
   * stay until then (`releaseKeys`), so they leave together with the padlocks.
   */
  private keysHeld = false;
  /** padlocks springing open right now (test hooks count them as still shown) */
  private opening = 0;

  constructor(
    private scene: Phaser.Scene,
    private L: CompiledLevel,
    private lay: PlayLayout,
    round: Round,
    /** Clearer items: the art each item is drawn with (looks.ts); every item as itself by default */
    readonly looks: ItemLooks = NO_LOOKS,
  ) {
    this.views = new Array(L.n).fill(null);
    this.pairs = gluePairs(L);
    this.tray = scene.add.graphics().setDepth(5);
    this.drawTray();
    for (let d = 0; d < L.nDisp; d++) {
      const first = L.dispStart[d]!;
      const p = piecePos(lay, L, first);
      this.machines.push(new MachineView(scene, p.x, p.y, lay.tile, this.depthOf(first)));
    }
    for (let k = 0; k < L.nTrays; k++) {
      const first = L.trayStart[k]!;
      const p = piecePos(lay, L, first);
      this.trays.push(trayState(round, k).shown ? new TrayView(scene, p.x, p.y, lay.tile, this.depthOf(first)) : null);
    }
    for (let i = 0; i < L.nBoard + L.nCrates; i++) {
      if (round.piece[i] !== PIECE_BOARD) continue;
      const v = new TileView(scene, i, L.kind[i] === Kind.Crate, lay.tile);
      const p = piecePos(lay, L, i);
      v.setPosition(p.x, p.y);
      v.setDepth(this.depthOf(i));
      this.views[i] = v;
    }
    this.refresh(round, false);
  }

  private drawTray(): void {
    const g = this.tray;
    const b = this.lay.board;
    const T = this.lay.tile;
    let minX = Infinity;
    let maxX = -Infinity;
    let minY = Infinity;
    let maxY = -Infinity;
    for (const i of boardPieces(this.L)) {
      const p = piecePos(this.lay, this.L, i);
      minX = Math.min(minX, p.x);
      maxX = Math.max(maxX, p.x);
      minY = Math.min(minY, p.y);
      maxY = Math.max(maxY, p.y);
    }
    if (!Number.isFinite(minX)) return;
    const pad = T * 0.34;
    const x = Math.max(b.x, minX - T / 2 - pad);
    const y = minY - T / 2 - pad;
    const w = Math.min(b.x + b.w, maxX + T / 2 + pad) - x;
    const h = maxY + T / 2 + pad * 1.2 - y;
    g.fillStyle(0x4a2c2a, 0.12);
    g.fillRoundedRect(x, y + T * 0.08, w, h, T * 0.45);
    g.fillStyle(0xfff3df, 0.55);
    g.fillRoundedRect(x, y, w, h, T * 0.45);
  }

  depthOf(i: number): number {
    return 100 + this.L.layer[i]! * 200 + this.L.y[i]! * 8 + this.L.x[i]! * 0.1;
  }

  /** Sync every board view with core state (covered, wrap, ice, crate hp, item). */
  refresh(r: Round, animate: boolean): void {
    for (let i = 0; i < this.views.length; i++) {
      const v = this.views[i];
      if (!v || machineOf(this.L, i) >= 0) continue;
      if (v.isCrate) {
        v.setCrateHp(r.hp[i]!);
        v.setCovered(r.cover[i]! > 0, animate);
        continue;
      }
      if (!v.item || v.getData('item') !== r.item[i]) {
        v.setItem(this.looks.of(this.L.items[r.item[i]!]!));
        v.setData('item', r.item[i]);
      }
      v.setWrapped(!isRevealed(r, i), animate);
      v.setIce(r.ice[i]!, animate);
      // collectGoal: the frame is the piece's, whatever item a shuffle put under it
      v.setGolden(isGoldenPiece(this.L, i) ? 'board' : null);
      v.setCovered(r.cover[i]! > 0, animate);
    }
    this.syncRibbons(r, animate);
    this.syncMachines(r, animate);
    this.syncTrays(r, animate);
    this.syncLocks(r, animate);
  }

  /**
   * Trays follow core state: the item they hold (an assist swap relabels all three together) and
   * the covered dimming (lockKey's badge comes with the other key pieces' in syncLocks). A popped
   * tray is the scene's to animate (`popTray`, from the pop's event); one core took without it just goes.
   */
  private syncTrays(r: Round, animate: boolean): void {
    this.trays.forEach((t, k) => {
      if (!t || t.gone) return;
      const st = trayState(r, k);
      if (!st.shown) {
        this.trays[k] = null;
        return void t.root.setVisible(false);
      }
      t.setItem(this.looks.of(this.L.items[st.item]!));
      t.setCovered(st.covered, animate);
    });
  }

  /**
   * tray: its three items lift and pop together at the tray (never through the bag); `onPop` fires
   * at the burst with its position (always after this call returns, as a bag pop's does), then the
   * empty tray folds away. The pop is tracked until it has played (`idle`).
   */
  popTray(k: number, onPop: (x: number, y: number) => void): Promise<void> {
    const t = this.trays[k];
    this.trays[k] = null;
    this.trayPops++;
    const p = this.pos(this.L.trayStart[k]!);
    const job = t ? t.pop(onPop, this.onTrayGone) : Promise.resolve().then(() => onPop(p.x, p.y));
    this.trayJobs.add(job);
    void job.finally(() => this.trayJobs.delete(job));
    return job;
  }

  /** Resolves once every tray pop in progress has played (the win and fail flows wait for it, as for the bag's pops). */
  async idle(): Promise<void> {
    while (this.trayJobs.size) await Promise.all([...this.trayJobs]);
  }

  /** Is piece i drawn on the board: its tile, or the tray it belongs to (test hooks)? */
  drawn(i: number): boolean {
    const k = trayOf(this.L, i);
    return k >= 0 ? !!this.trays[k]?.shown : !!this.views[i];
  }

  /** Trays drawn on the board, and pops played (test hooks). */
  trayInfo(): { drawn: number; popped: number } {
    return { drawn: this.trays.filter((t) => t?.shown).length, popped: this.trayPops };
  }

  /** Win sweep: the leftover trays fade with the tiles. */
  clearTrays(): void {
    for (const t of this.trays) t?.fadeOut();
  }

  /** lockKey: a key piece in the view's eyes (core's, or held until the key pops on screen). */
  keyShown(r: Round, i: number): boolean {
    if (isKeyPiece(r, i)) return true;
    return this.keysHeld && r.L.lockKey >= 0 && r.piece[i] === PIECE_BOARD && r.item[i] === r.L.lockKey && isRevealed(r, i);
  }

  /**
   * LockKey: padlocks on every locked tile while the locks are shut (they only come off through
   * `openPadlocks`, after the key's pop), and the key badge on every piece of the key item.
   */
  private syncLocks(r: Round, animate: boolean): void {
    if (!hasLocks(r)) return;
    for (const i of padlockTiles(r)) this.views[i]?.setPadlock();
    for (let i = 0; i < this.views.length; i++) {
      const v = this.views[i];
      if (v && !v.isCrate) v.setKey(this.keyShown(r, i), animate);
    }
    // a tray of the key item wears it too (its first piece speaks for all three)
    this.trays.forEach((t, k) => {
      if (t && !t.gone) t.setKey(this.keyShown(r, this.L.trayStart[k]!));
    });
  }

  /** lockKey: the unlock is in this batch; keep the key badges until the key pops on screen. */
  holdKeys(): void {
    this.keysHeld = true;
  }

  /** lockKey: the key popped on screen; the badges fade (the padlocks open with `openPadlocks`). */
  releaseKeys(r: Round, animate: boolean): void {
    this.keysHeld = false;
    this.syncLocks(r, animate);
  }

  /** World position of a tile's padlock (the key sparkle flies there), or null. */
  padlockPos(i: number): { x: number; y: number } | null {
    const p = this.views[i]?.padlock;
    if (!p) return null;
    const m = p.getWorldTransformMatrix();
    return { x: m.tx, y: m.ty };
  }

  /** lockKey: open one tile's padlock; `onPop` fires as it comes off. */
  openPadlock(i: number, onPop: ((x: number, y: number) => void) | null): Promise<void> {
    const v = this.views[i];
    if (!v?.padlock) return Promise.resolve();
    this.opening++;
    return v.openPadlock(onPop).then(() => {
      this.opening--;
    });
  }

  /** Padlocks drawn (opening ones included) and key badges shown on the board (test hooks). */
  lockInfo(): { padlocks: number; keys: number } {
    let padlocks = this.opening;
    let keys = 0;
    for (const v of this.views) {
      if (v?.padlock) padlocks++;
      if (v?.keyBadge) keys++;
    }
    for (const t of this.trays) if (t?.keyBadge) keys++;
    return { padlocks, keys };
  }

  /** collectGoal: gold frames drawn on the board (test hooks). */
  get goldFrames(): number {
    return this.views.filter((v) => v?.goldFrame).length;
  }

  /** Mechanic intro: the golden frames glint one after another once play starts (decorative). */
  shineGolden(): void {
    let k = 0;
    for (const v of this.views) if (v?.goldFrame) v.shineGolden(k++ * 90);
  }

  /** Mechanic intro: the padlocks wiggle once play starts (decorative). */
  wigglePadlocks(): void {
    for (const v of this.views) v?.padlock?.wiggle();
  }

  /** Feedback (a magnet held back by the locks): every padlock rattles. */
  rattlePadlocks(): void {
    for (const v of this.views) v?.padlock?.rattle();
  }

  /**
   * Dispensers follow core state: the machine shows its top item (a new one rises into the
   * window after a tap or magnet, clipped to the glass), the counter, and the covered dimming
   * (with the awning peeking out above the covering tile); an item an undo put
   * back on top pushes the shown one back down; an empty machine folds away (and returns with
   * an undo of its last item).
   */
  private syncMachines(r: Round, animate: boolean): void {
    const L = this.L;
    this.machines.forEach((m, d) => {
      const st = machineState(r, d);
      for (let i = L.dispStart[d]!; i < L.dispStart[d + 1]!; i++) {
        const v = this.views[i];
        if (!v || i === st.top) continue;
        this.views[i] = null;
        m.sink(v, animate);
      }
      if (st.gone) {
        m.left = 0; // the badge keeps its last number while the machine folds away
        return m.collapse(animate, this.onMachineGone);
      }
      m.restore(animate);
      let v = this.views[st.top];
      if (!v) {
        v = new TileView(this.scene, st.top, false, m.itemSize);
        v.setBare(true);
        v.setDepth(this.depthOf(st.top));
        this.views[st.top] = v;
        m.slideIn(v, animate);
      }
      if (!v.item || v.getData('item') !== r.item[st.top]) {
        v.setItem(this.looks.of(L.items[r.item[st.top]!]!));
        v.setData('item', r.item[st.top]);
      }
      v.setCovered(st.covered, animate);
      v.setKey(this.keyShown(r, st.top), animate);
      m.setCovered(st.covered, animate);
      m.setLeft(st.left, animate);
    });
  }

  /** Dispenser machines: items left, the badge text, shown and dimmed (test hooks). */
  machineInfo(r: Round): { left: number; counter: string; shown: boolean; covered: boolean; item: number }[] {
    return this.machines.map((m, d) => {
      const top = machineState(r, d).top;
      return { left: m.left, counter: m.counter, shown: m.shown, covered: m.covered, item: top >= 0 && this.views[top] ? r.item[top]! : -1 };
    });
  }

  /** Win sweep: the machines fade with the leftover tiles. */
  clearMachines(): void {
    for (const m of this.machines) if (!m.gone) m.fadeOut();
  }

  /** Soft "nope" for a tap that core refuses (a covered tile, or a covered machine). */
  nope(i: number): void {
    const d = machineOf(this.L, i);
    if (d >= 0) return this.machines[d]!.nope(this.views[i] ?? null);
    const k = trayOf(this.L, i);
    if (k >= 0) return this.trays[k]?.nope();
    this.views[i]?.nope();
  }

  /** lockKey: a tap on a padlocked tile — the tile wobbles and its padlock rattles. */
  lockNope(i: number): void {
    this.views[i]?.lockNope();
  }

  /**
   * Glue ribbons follow core state: one per pair with both tiles on the board, dimmed while
   * either is covered, snapped when either leaves (a tap, magnet, or the partner going alone).
   */
  private syncRibbons(r: Round, animate: boolean): void {
    for (const [a, b] of this.pairs) {
      const st = ribbonState(r, a, b);
      const on = st.on && !!this.views[a] && !!this.views[b];
      let rb = this.ribbons.get(a);
      if (!on) {
        if (rb) {
          this.ribbons.delete(a);
          rb.snap(animate ? this.onRibbonSnap : null);
        }
        continue;
      }
      if (!rb) {
        // tilt a little, alternating, so the tape reads as stuck on by hand
        rb = new GlueRibbon(this.scene, this.pos(a), this.pos(b), this.lay.tile, (a + b) % 2 ? -6 : 5);
        rb.setDepth(Math.max(this.depthOf(a), this.depthOf(b)) + 0.05);
        // back from the bag (undo, rescue): stick on once the tiles have landed
        if (animate) rb.appear(dur(220));
        this.ribbons.set(a, rb);
      }
      rb.setCovered(st.covered, animate);
    }
  }

  /** Win sweep: the leftover ribbons fade with their tiles. */
  clearRibbons(): void {
    for (const rb of this.ribbons.values()) rb.fadeOut();
    this.ribbons.clear();
  }

  /** Ribbon count and dimmed count (test hooks). */
  ribbonInfo(): { shown: number; covered: number } {
    let covered = 0;
    for (const rb of this.ribbons.values()) if (rb.covered) covered++;
    return { shown: this.ribbons.size, covered };
  }

  /** Topmost board piece under a device-px point (logical rects, not animated sprites). */
  hitTest(x: number, y: number): number {
    let best = -1;
    let bestDepth = -Infinity;
    const half = this.lay.tile * 0.48;
    for (let i = 0; i < this.views.length; i++) {
      if (!this.views[i]) continue;
      const p = piecePos(this.lay, this.L, i);
      if (Math.abs(x - p.x) > half || Math.abs(y - p.y) > half) continue;
      const d = this.depthOf(i);
      if (d > bestDepth) {
        bestDepth = d;
        best = i;
      }
    }
    // a tray is one cell: a hit anywhere on it takes its first piece
    this.trays.forEach((t, k) => {
      if (!t?.shown) return;
      const i = this.L.trayStart[k]!;
      const p = piecePos(this.lay, this.L, i);
      if (Math.abs(x - p.x) > half || Math.abs(y - p.y) > half) return;
      const d = this.depthOf(i);
      if (d > bestDepth) {
        bestDepth = d;
        best = i;
      }
    });
    return best;
  }

  /** Take a piece's view off the board (it flies to the bag). */
  detach(i: number): TileView | null {
    const v = this.views[i] ?? null;
    this.views[i] = null;
    if (v) {
      // a dispenser item may still be rising into its window: the bag's flight owns it now
      this.scene.tweens.killTweensOf(v);
      v.clearMask();
      v.setAlpha(1);
      v.lightUp();
      v.setAngle(0);
      v.setBare(false);
      // lockKey: the badge marks key pieces on the board only (a returning piece gets it back in refresh)
      v.setKey(false, false);
      // collectGoal: a golden piece keeps only its star corner off the board (the frame comes back in refresh)
      v.setGolden(isGoldenPiece(this.L, i) ? 'bag' : null);
    }
    return v;
  }

  /** Put a view back on the board (undo / rescue) with a flight to its spot (a dispenser item: its machine's window). */
  attach(i: number, v: TileView, animate: boolean): void {
    this.views[i] = v;
    const m = this.machines[machineOf(this.L, i)];
    const p = m ? m.window() : piecePos(this.lay, this.L, i);
    // a machine's item is drawn smaller than a tile (the bag may have made it at tile size)
    const scale = m ? m.itemSize / v.size : 1;
    const d = animate ? dur(220) : 0;
    this.scene.tweens.killTweensOf(v);
    v.setAlpha(1);
    v.setBare(!!m);
    if (!d) {
      v.setPosition(p.x, p.y).setScale(scale).setDepth(this.depthOf(i));
      return;
    }
    v.setDepth(6000);
    this.scene.tweens.add({
      targets: v,
      x: p.x,
      y: p.y,
      scale,
      duration: d,
      ease: 'Cubic.easeOut',
      onComplete: () => v.setDepth(this.depthOf(i)),
    });
  }

  pos(i: number): { x: number; y: number } {
    return piecePos(this.lay, this.L, i);
  }

  /** Remaining board tiles (for the win sweep). */
  remaining(): TileView[] {
    return this.views.filter((v): v is TileView => !!v && !v.isCrate);
  }

  destroyPiece(i: number): TileView | null {
    const v = this.views[i] ?? null;
    this.views[i] = null;
    return v;
  }
}
